import axios from "axios";
import OAUTH_CONFIG from "../config/oauth.config";

/**
 * OAuth Service
 * Handles all OAuth authentication including iframe support
 */
class OAuthService {
  constructor() {
    this.tokenKey = "wp_oauth_token";
    this.refreshTokenKey = "wp_oauth_refresh_token";
    this.userKey = "wp_oauth_user";
    this.expiryKey = "wp_oauth_expiry";
    this.stateKey = "oauth_state";
  }

  /**
   * Check if running in iframe
   */
  isInIframe() {
    return OAUTH_CONFIG.isEmbedded;
  }

  /**
   * Main authentication method
   * Returns { success: boolean, user?: object, requiresLogin?: boolean }
   */
  async authenticate() {
    try {
      console.log("🔐 Starting authentication check...");

      // Step 1: Check existing token
      if (this.isAuthenticated()) {
        console.log("✅ Valid token found");
        return { success: true, user: this.getUser() };
      }

      // Step 2: Try silent authentication
      console.log("🔍 Attempting silent authentication...");
      const silentResult = await this.trySilentAuth();

      if (silentResult.success) {
        console.log("✅ Silent authentication successful");
        return silentResult;
      }

      // Step 3: No valid session
      console.log("❌ Authentication required");
      return {
        success: false,
        requiresLogin: true,
        error: "authentication_required",
      };
    } catch (error) {
      console.error("Authentication error:", error);
      return {
        success: false,
        requiresLogin: true,
        error: error.message,
      };
    }
  }

  /**
   * Silent Authentication using hidden iframe
   * This enables auto-login if user is already logged into WordPress
   */
  async trySilentAuth() {
    return new Promise((resolve) => {
      const timeoutDuration = 5000;

      // Create hidden iframe
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.id = "oauth-silent-auth-iframe";

      // Generate state for CSRF protection
      const state = this.generateState();

      // Build OAuth URL with prompt=none (silent auth)
      const authUrl = this.buildAuthUrl({
        redirectUri: OAUTH_CONFIG.silentRedirectUri,
        prompt: "none",
        state: state,
      });

      // Listen for message from iframe
      const messageHandler = async (event) => {
        // Security: verify origin
        if (event.origin !== window.location.origin) return;

        if (event.data.type === "SILENT_AUTH_SUCCESS") {
          console.log("✅ Silent auth success, exchanging code...");
          cleanup();

          try {
            const result = await this.handleCallback(event.data.code, state);
            resolve(result);
          } catch (error) {
            console.error("Token exchange failed:", error);
            resolve({ success: false, error: error.message });
          }
        } else if (event.data.type === "SILENT_AUTH_FAILED") {
          console.log("❌ Silent auth failed:", event.data.error);
          cleanup();
          resolve({ success: false, error: event.data.error });
        }
      };

      // Cleanup function
      const cleanup = () => {
        window.removeEventListener("message", messageHandler);
        if (document.body.contains(iframe)) {
          document.body.removeChild(iframe);
        }
      };

      // Setup timeout
      const timeout = setTimeout(() => {
        console.log("⏱️ Silent auth timeout");
        cleanup();
        resolve({ success: false, error: "timeout" });
      }, timeoutDuration);

      // Add message listener
      window.addEventListener("message", messageHandler);

      // Load OAuth URL in iframe
      iframe.onload = () => {
        console.log("📄 Silent auth iframe loaded");
      };

      iframe.onerror = () => {
        console.error("❌ Silent auth iframe error");
        clearTimeout(timeout);
        cleanup();
        resolve({ success: false, error: "iframe_load_error" });
      };

      iframe.src = authUrl;
      document.body.appendChild(iframe);
    });
  }

  /**
   * Build OAuth authorization URL
   */
  buildAuthUrl(options = {}) {
    const params = new URLSearchParams({
      client_id: OAUTH_CONFIG.clientId,
      redirect_uri: options.redirectUri || OAUTH_CONFIG.redirectUri,
      response_type: OAUTH_CONFIG.responseType,
      scope: OAUTH_CONFIG.scope,
      state: options.state || this.generateState(),
    });

    if (options.prompt) {
      params.append("prompt", options.prompt);
    }

    return `${OAUTH_CONFIG.authorizationEndpoint}?${params.toString()}`;
  }

  /**
   * Redirect to WordPress login
   * Handles iframe scenario by opening in parent window
   */
  redirectToLogin() {
    const state = this.generateState();
    sessionStorage.setItem(this.stateKey, state);

    const authUrl = this.buildAuthUrl({ state });

    if (this.isInIframe()) {
      console.log("📱 Opening login in parent window (iframe detected)");
      window.parent.location.href = authUrl;
    } else {
      console.log("🔄 Redirecting to WordPress login");
      window.location.href = authUrl;
    }
  }

  /**
   * Handle OAuth callback (exchange code for token)
   */
  async handleCallback(code, state) {
    try {
      // Verify state (CSRF protection)
      const savedState = sessionStorage.getItem(this.stateKey);
      if (state && savedState && state !== savedState) {
        throw new Error("Invalid state parameter - possible CSRF attack");
      }
      sessionStorage.removeItem(this.stateKey);

      console.log("🔄 Exchanging authorization code for token...");

      // Exchange code for access token
      const tokenData = await this.exchangeCodeForToken(code);

      // Store tokens
      this.setTokens(tokenData);

      // Get user info
      const user = await this.getUserInfo(tokenData.access_token);
      this.setUser(user);

      console.log("✅ Authentication complete");

      return { success: true, user };
    } catch (error) {
      console.error("❌ OAuth callback failed:", error);
      throw error;
    }
  }

  /**
   * Exchange authorization code for access token
   */
  async exchangeCodeForToken(code) {
    try {
      const response = await axios.post(
        OAUTH_CONFIG.tokenEndpoint,
        new URLSearchParams({
          grant_type: "authorization_code",
          code: code,
          client_id: OAUTH_CONFIG.clientId,
          client_secret: OAUTH_CONFIG.clientSecret,
          redirect_uri: OAUTH_CONFIG.redirectUri,
        }),
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error("Token exchange failed:", error.response?.data || error);
      throw new Error("Failed to exchange authorization code for token");
    }
  }

  /**
   * Get user information from WordPress
   */
  async getUserInfo(accessToken) {
    try {
      const response = await axios.get(OAUTH_CONFIG.userInfoEndpoint, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      return response.data;
    } catch (error) {
      console.error("Failed to get user info:", error);
      throw new Error("Failed to retrieve user information");
    }
  }

  /**
   * Token Management
   */
  setTokens(tokenData) {
    localStorage.setItem(this.tokenKey, tokenData.access_token);

    if (tokenData.refresh_token) {
      localStorage.setItem(this.refreshTokenKey, tokenData.refresh_token);
    }

    if (tokenData.expires_in) {
      const expiryTime = Date.now() + tokenData.expires_in * 1000;
      localStorage.setItem(this.expiryKey, expiryTime.toString());
    }
  }

  getToken() {
    return localStorage.getItem(this.tokenKey);
  }

  isTokenValid() {
    const token = this.getToken();
    const expiry = localStorage.getItem(this.expiryKey);

    if (!token) return false;
    if (!expiry) return true;

    return Date.now() < parseInt(expiry);
  }

  /**
   * User Management
   */
  setUser(user) {
    localStorage.setItem(this.userKey, JSON.stringify(user));
  }

  getUser() {
    const user = localStorage.getItem(this.userKey);
    return user ? JSON.parse(user) : null;
  }

  /**
   * Authentication Check
   */
  isAuthenticated() {
    return this.getToken() && this.isTokenValid();
  }

  /**
   * Logout
   */
  logout() {
    this.clearAuth();

    const logoutUrl = `${
      OAUTH_CONFIG.wordpressUrl
    }/wp-login.php?action=logout&redirect_to=${encodeURIComponent(
      window.location.origin
    )}`;

    if (this.isInIframe()) {
      window.parent.location.href = logoutUrl;
    } else {
      window.location.href = logoutUrl;
    }
  }

  /**
   * Clear authentication data
   */
  clearAuth() {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.refreshTokenKey);
    localStorage.removeItem(this.userKey);
    localStorage.removeItem(this.expiryKey);
    sessionStorage.removeItem(this.stateKey);
  }

  /**
   * Generate random state for CSRF protection
   */
  generateState() {
    return (
      Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15)
    );
  }

  /**
   * Make authenticated API request
   */
  async apiRequest(url, options = {}) {
    const token = this.getToken();

    if (!token) {
      throw new Error("No authentication token available");
    }

    return axios({
      url,
      ...options,
      headers: {
        ...options.headers,
        Authorization: `Bearer ${token}`,
      },
    });
  }
}

export default new OAuthService();
